import React from "react";
import {
  AutocompleteInput,
  Datagrid,
  Filter,
  FunctionField,
  ListProps,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  TextField,
} from "react-admin";
import { getMonth, SchoolIdSelected } from "utils/tools";
import CustomPagination from "../../components/CustomPagination";
import CustomMonthPickerInput from "../../components/CustomMonthPickerInput";
import { Tooltip } from "@material-ui/core";
import CustomDateField from "../../components/CustomDateField";
import UpdatedByField from "../../components/UpdatedByField";
import DeleteButton from "./components/DeleteButton";
import { StyledButton } from "../../components/ListShowButton";
import ListWrapper from "../../components/ListWrapper";
import ActionsMenu from "../../components/ActionsMenu";

const ExcludeBusAssistantFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />

    <ReferenceInput
      reference="subcontractors"
      source="SubcontractorId"
      filterToQuery={(searchText) => {
        if (searchText !== "") return { search: searchText };
      }}
      sort={{ field: "name", order: "ASC" }}
      alwaysOn
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>

    <CustomMonthPickerInput
      label="Excluded Month"
      name="ExcludedMonth"
      source="ExcludedMonth"
      year={props.filterValues.year}
      month={props.filterValues.month}
      showClearButton
      alwaysOn
    />
  </Filter>
);

const EditButton = (record: IExcludeBusAssistant) => {
  // Check if the existing excludeBusAssistant month/year
  // is in the past compared to current month/year.
  // This prevents to edit exclude bus assistant for past months
  const today = new Date();
  const isInvalidMonthToEdit =
    record.year < today.getFullYear() ||
    (record.year === today.getFullYear() &&
      record.month < today.getMonth() + 1);

  const tooltipLabel = isInvalidMonthToEdit
    ? "Edits allowed only for current or future months"
    : "Edit Exclude Bus Assistant";

  return (
    <Tooltip title={tooltipLabel}>
      {/*Wrap button in <span> to allows the tooltip to show even when the button is disabled*/}
      <span>
        <StyledButton
          variant="outlined"
          href={`/#/excludeBusAssistants/${record.id}`}
          disabled={isInvalidMonthToEdit}
        >
          Edit
        </StyledButton>
      </span>
    </Tooltip>
  );
};

const ExcludeBusAssistantList = (props: ListProps) => {
  const breadcrumb = [
    { link: "/excludeBusAssistants", title: "Exclude Bus Assistants" },
  ];

  return (
    <>
      <ListWrapper
        headerTitle="Exclude Bus Assistants"
        breadcrumb={breadcrumb}
        {...props}
        exporter={false}
        filters={<ExcludeBusAssistantFilter />}
        filter={{
          SchoolId: Number(SchoolIdSelected()),
        }}
        sort={{ field: "createdAt", order: "DESC" }}
        bulkActionButtons={false}
        perPage={25}
        pagination={<CustomPagination />}
      >
        <Datagrid>
          <TextField source="id" />
          <ReferenceField
            label="Route Plan Wave"
            source="busSchedulePlanId"
            reference="busSchedulePlans"
            link={false}
          >
            <ReferenceField
              source="busScheduleId"
              reference="busSchedules"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </ReferenceField>
          <ReferenceField
            label="Route"
            source="routeId"
            reference="routes"
            linkType={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor Identifier"
            reference="subcontractors"
            link={false}
          >
            <TextField source="subcontractorIdentifier" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor MBC Code"
            reference="subcontractors"
            link={false}
          >
            <TextField source="mbcVendorId" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            reference="subcontractors"
            link={false}
          >
            <TextField source="name" emptyText="-" />
          </ReferenceField>
          <FunctionField
            label="Excluded Month"
            render={(record: any) =>
              `${getMonth(record.month)} - ${record.year}`
            }
          />
          <FunctionField
            label="Created By"
            render={(record: any) => (
              <>
                {record.updatedBy === null ? (
                  <CustomDateField source="createdAt" isDateTime />
                ) : (
                  <>
                    <UpdatedByField data={record.createdBy} />
                    {" at "}
                    <CustomDateField source="createdAt" isDateTime />
                  </>
                )}
              </>
            )}
          />
          <FunctionField
            label="Last Updated By"
            render={(record: any) => (
              <>
                {record.updatedBy === null ? (
                  <CustomDateField source="updatedAt" isDateTime />
                ) : (
                  <>
                    <UpdatedByField data={record.updatedBy} />
                    {" at "}
                    <CustomDateField source="updatedAt" isDateTime />
                  </>
                )}
              </>
            )}
          />
          <FunctionField
            label="Actions"
            render={(record: any) => (
              <ActionsMenu
                render={[
                  <EditButton {...record} />,
                  <DeleteButton {...record} />,
                ]}
              />
            )}
          />
        </Datagrid>
      </ListWrapper>
    </>
  );
};

export default ExcludeBusAssistantList;
